import { Provider } from '@angular/core';
import { injectDispatch } from '@ngrx/signals/events';
import { TransactionEvents } from '@app/domain/events';
import {
  GetDebtorsUseCase,
  GetDebtorByIdUseCase,
  CountDebtorsUseCase,
  CreateDebtorUseCase,
  UpdateDebtorUseCase,
  DeleteDebtorUseCase,
  SetLoanClosedUseCase,
  GetDebtorPaymentsUseCase,
  CreateDebtorPaymentUseCase,
  UpdateDebtorPaymentUseCase,
  DeleteDebtorPaymentUseCase,
} from '@app/domain/use-cases';
import type { IDebtorRepository, IDebtorPaymentRepository } from '@app/domain/ports';
import { DEBTOR_REPOSITORY, DEBTOR_PAYMENT_REPOSITORY } from '../../tokens/infrastructure.tokens';

/**
 * Provides Debtor domain use cases.
 *
 * Includes:
 * - Debtor Use Cases: GetDebtors, GetDebtorById, Count, Create, Update, Delete
 * - Payment Use Cases: GetDebtorPayments, CreateDebtorPayment, UpdateDebtorPayment, DeleteDebtorPayment
 *
 * All providers use constructor injection pattern following Clean Architecture.
 */
export function provideDebtorDomain(): Provider[] {
  return [
    // Debtor Use Cases - Query
    {
      provide: GetDebtorsUseCase,
      useFactory: (repository: IDebtorRepository) => new GetDebtorsUseCase(repository),
      deps: [DEBTOR_REPOSITORY],
    },
    {
      provide: GetDebtorByIdUseCase,
      useFactory: (repository: IDebtorRepository) => new GetDebtorByIdUseCase(repository),
      deps: [DEBTOR_REPOSITORY],
    },
    {
      provide: CountDebtorsUseCase,
      useFactory: (repository: IDebtorRepository) => new CountDebtorsUseCase(repository),
      deps: [DEBTOR_REPOSITORY],
    },

    // Debtor Use Cases - Commands
    {
      provide: CreateDebtorUseCase,
      useFactory: (repository: IDebtorRepository) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new CreateDebtorUseCase(repository, dispatch);
      },
      deps: [DEBTOR_REPOSITORY],
    },
    {
      provide: UpdateDebtorUseCase,
      useFactory: (repository: IDebtorRepository) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new UpdateDebtorUseCase(repository, dispatch);
      },
      deps: [DEBTOR_REPOSITORY],
    },
    {
      provide: DeleteDebtorUseCase,
      useFactory: (repository: IDebtorRepository) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new DeleteDebtorUseCase(repository, dispatch);
      },
      deps: [DEBTOR_REPOSITORY],
    },
    {
      provide: SetLoanClosedUseCase,
      useFactory: (repository: IDebtorRepository) => new SetLoanClosedUseCase(repository),
      deps: [DEBTOR_REPOSITORY],
    },

    // Payment Use Cases - Query
    {
      provide: GetDebtorPaymentsUseCase,
      useFactory: (repository: IDebtorPaymentRepository) => new GetDebtorPaymentsUseCase(repository),
      deps: [DEBTOR_PAYMENT_REPOSITORY],
    },

    // Payment Use Cases - Commands
    {
      provide: CreateDebtorPaymentUseCase,
      useFactory: (paymentRepository: IDebtorPaymentRepository, debtorRepository: IDebtorRepository) =>
        new CreateDebtorPaymentUseCase(paymentRepository, debtorRepository),
      deps: [DEBTOR_PAYMENT_REPOSITORY, DEBTOR_REPOSITORY],
    },
    {
      provide: UpdateDebtorPaymentUseCase,
      useFactory: (paymentRepository: IDebtorPaymentRepository, debtorRepository: IDebtorRepository) =>
        new UpdateDebtorPaymentUseCase(paymentRepository, debtorRepository),
      deps: [DEBTOR_PAYMENT_REPOSITORY, DEBTOR_REPOSITORY],
    },
    {
      provide: DeleteDebtorPaymentUseCase,
      useFactory: (paymentRepository: IDebtorPaymentRepository, debtorRepository: IDebtorRepository) =>
        new DeleteDebtorPaymentUseCase(paymentRepository, debtorRepository),
      deps: [DEBTOR_PAYMENT_REPOSITORY, DEBTOR_REPOSITORY],
    },
  ];
}
